import { prisma } from '../../shared/prisma';
import { AppError } from '../../shared/errors/AppError';

export async function createTeam(workspaceId: string, userId: string, data: { name: string; description?: string; isPrivate?: boolean; memberIds?: string[] }) {
  if (!workspaceId || workspaceId === 'default' || workspaceId === 'all') {
    const ws = await prisma.workspace.findFirst();
    if (!ws) throw new AppError(400, 'No workspace found');
    workspaceId = ws.id;
  }

  const existingTeam = await prisma.team.findFirst({
    where: {
      workspaceId,
      name: { equals: data.name, mode: 'insensitive' }
    }
  });

  if (existingTeam) {
    throw new AppError(400, `A team named "${data.name}" already exists in this workspace.`);
  }

  const allMembers = [
    { userId, role: 'owner' },
    ...(data.memberIds || []).filter(id => id !== userId).map(id => ({ userId: id, role: 'member' }))
  ];
  
  const { memberIds, workspaceId: _discardedWsId, ...restData } = data as any;

  const team = await prisma.team.create({
    data: { 
      ...restData, 
      workspaceId,
      members: { create: allMembers },
      channels: { create: { name: 'general', description: 'General discussion', isPrivate: false, members: { create: allMembers } } }
    },
    include: { 
      _count: { select: { members: true, channels: true } }, 
      channels: { where: { isArchived: false } },
      members: { include: { user: { select: { id: true, displayName: true, avatarUrl: true, presence: true, email: true, username: true } } } }
    },
  });
  return team;
}

export async function listTeams(workspaceId: string, userId: string) {
  const userRoles = await prisma.userRole.findMany({
    where: { userId },
    include: { role: true },
  });
  const isAdmin = userRoles.some(ur => ur.role.name === 'admin' || ur.role.name === 'super_admin');

  const where: any = {};
  if (!isAdmin) {
    where.members = { some: { userId } };
  } else {
    if (workspaceId && workspaceId !== 'all') {
      where.workspaceId = workspaceId;
    }
  }

  return prisma.team.findMany({
    where,
    include: { 
      _count: { select: { members: true, channels: true } }, 
      channels: { where: { isArchived: false } },
      members: { include: { user: { select: { id: true, displayName: true, avatarUrl: true, presence: true, email: true, username: true } } } }
    },
    orderBy: { name: 'asc' },
  });
}

export async function getTeam(teamId: string, userId: string) {
  const team = await prisma.team.findUnique({
    where: { id: teamId },
    include: {
      members: { include: { user: { select: { id: true, displayName: true, avatarUrl: true, presence: true } } } },
      channels: { where: { isArchived: false } },
    },
  });
  if (!team) throw new AppError(404, 'Team not found');
  return team;
}

export async function addMember(teamId: string, userId: string) {
  return prisma.teamMember.upsert({
    where: { teamId_userId: { teamId, userId } },
    update: {},
    create: { teamId, userId, role: 'member' },
  });
}

export async function removeMember(teamId: string, userId: string) {
  return prisma.teamMember.delete({ where: { teamId_userId: { teamId, userId } } });
}

export async function updateTeam(teamId: string, userId: string, data: { name?: string; description?: string; avatarUrl?: string }) {

  if (data.name) {
    const team = await prisma.team.findUnique({ where: { id: teamId } });
    if (team) {
      const existing = await prisma.team.findFirst({
        where: { workspaceId: team.workspaceId, name: { equals: data.name, mode: 'insensitive' }, id: { not: teamId } }
      });
      if (existing) throw new AppError(400, `A team named "${data.name}" already exists.`);
    }
  }

  return prisma.team.update({
    where: { id: teamId },
    data: { name: data.name, description: data.description, avatarUrl: data.avatarUrl },
    include: { 
      _count: { select: { members: true, channels: true } }, 
      channels: { where: { isArchived: false } },
      members: { include: { user: { select: { id: true, displayName: true, avatarUrl: true, presence: true, email: true, username: true } } } }
    },
  });
}

export async function deleteTeam(teamId: string, userId: string) {
  // Find all channels to manually clean up messages
  const channels = await prisma.channel.findMany({ where: { teamId }, select: { id: true } });
  const channelIds = channels.map(c => c.id);

  if (channelIds.length > 0) {
    // Delete replies first to prevent self-referencing FK violations
    await prisma.message.deleteMany({
      where: { channelId: { in: channelIds }, parentId: { not: null } }
    });
    // Delete top-level messages
    await prisma.message.deleteMany({
      where: { channelId: { in: channelIds } }
    });
  }

  return prisma.team.delete({ where: { id: teamId } });
}
